import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateSupplier, useUpdateSupplier } from '../../mutations/supplierMutations';
import { usePortLoadings } from '../../queries/portLoadingQueries';
import { useCreatePortLoading } from '../../mutations/portLoadingMutations';

// ─── Constants ────────────────────────────────────────────────────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]{7,20}$/;

const EMPTY_BANK = {
  id: 0,
  bankName: '',
  accountHolderName: '',
  accountNumber: '',
  swiftCode: '',
  accountHolderAddress: '',
  bankAddress: '',
  isPrimary: false,
};

// ─── Default Values (matches full API schema) ─────────────────────────────────
const defaultValues = {
  supplierId: '',
  supplierName: '',
  ownerName: '',
  mobileNumber: '',
  emailId: '',
  panNumber: '',
  website: '',
  adCode: '',
  lutNumber: '',
  lutExpiryDate: null,
  rcmcNumber: '',
  paymentTerms: '',
  incoterms: '',
  origin: '',
  foreignBankCharge: '',
  isActive: true,
  bankDetails: [{ ...EMPTY_BANK }],
};

// ─── Reusable: Controlled Input ───────────────────────────────────────────────
const Field = ({ id, label, name, control, rules = {}, type = 'text', placeholder = '', errors }) => (
  <div className="mb-2">
    <label htmlFor={id} className="form-label fw-semibold small text-uppercase text-dark mb-1">
      {label}
      {rules.required && <span className="text-danger ms-1" aria-hidden="true">*</span>}
    </label>
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <input
          {...field}
          id={id}
          type={type}
          className={`form-control${errors ? ' is-invalid' : ''}`}
          placeholder={placeholder}
          aria-label={label}
          aria-invalid={!!errors}
          aria-describedby={errors ? `${id}-err` : undefined}
        />
      )}
    />
    {errors && (
      <div id={`${id}-err`} className="invalid-feedback d-block" role="alert">
        {errors.message}
      </div>
    )}
  </div>
);

// ─── Reusable: Section Divider ────────────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="mb-3">
    <h2 className="text-center fw-bold text-uppercase fs-6 py-2 mb-2 border-bottom border-top gtss-text-primary">
      {title}
    </h2>
    {children}
  </div>
);

// ─── Add Origin Modal ─────────────────────────────────────────────────────────
const AddOriginModal = ({ show, onClose, onSuccess }) => {
  const createPortLoading = useCreatePortLoading();

  const {
    control: modalControl,
    handleSubmit: modalHandleSubmit,
    reset: modalReset,
    formState: { errors: modalErrors },
  } = useForm({ defaultValues: { country: '', countryCode: '' } });

  const onModalSubmit = (data) => {
    createPortLoading.mutate(data, {
      onSuccess: () => {
        modalReset();
        onSuccess();
        onClose();
      },
    });
  };

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-origin-modal-title"
        style={{ zIndex: 1055 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px', overflow: 'hidden' }}>

            {/* Header */}
            <div
              className="modal-header border-0 text-white px-4 py-3"
              style={{ background: '#1A237E' }}
            >
              <h5
                className="modal-title fw-bold text-uppercase mb-0"
                id="add-origin-modal-title"
                style={{ fontSize: '14px', letterSpacing: '0.6px' }}
              >
                🌍 Add New Origin
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                aria-label="Close"
                onClick={onClose}
              />
            </div>

            {/* Body — using div instead of form to prevent nested-form page reload */}
            <div>
              <div className="modal-body px-4 py-4">
                <div className="mb-3">
                  <label
                    htmlFor="modal-country"
                    className="form-label fw-semibold small text-uppercase text-dark"
                  >
                    Country <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="country"
                    control={modalControl}
                    rules={{ required: 'Country is required.' }}
                    render={({ field }) => (
                      <input
                        {...field}
                        id="modal-country"
                        type="text"
                        className={`form-control${modalErrors.country ? ' is-invalid' : ''}`}
                        placeholder="e.g. India"
                      />
                    )}
                  />
                  {modalErrors.country && (
                    <div className="invalid-feedback d-block">{modalErrors.country.message}</div>
                  )}
                </div>

                <div className="mb-1">
                  <label
                    htmlFor="modal-countryCode"
                    className="form-label fw-semibold small text-uppercase text-dark"
                  >
                    Country Code
                  </label>
                  <Controller
                    name="countryCode"
                    control={modalControl}
                    render={({ field }) => (
                      <input
                        {...field}
                        id="modal-countryCode"
                        type="text"
                        className="form-control"
                        placeholder="e.g. IN"
                        style={{ textTransform: 'uppercase' }}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer border-0 px-4 pb-4 pt-0 d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light fw-semibold px-4"
                  onClick={onClose}
                  style={{ borderRadius: '8px' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  id="add-origin-submit-btn"
                  className="btn fw-semibold text-white px-4 border-0"
                  disabled={createPortLoading.isPending}
                  onClick={modalHandleSubmit(onModalSubmit)}
                  style={{ background: '#1A237E', borderRadius: '8px' }}
                >
                  {createPortLoading.isPending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Saving...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

// ─── Origin Searchable Dropdown ───────────────────────────────────────────────
const OriginDropdown = ({ value, onChange, errors }) => {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const wrapperRef = useRef(null);

  const { data: rawPortLoadings, refetch } = usePortLoadings();
  // Safe fallback: API may return array, { data: [] }, or null
  const portLoadings = Array.isArray(rawPortLoadings)
    ? rawPortLoadings
    : Array.isArray(rawPortLoadings?.data)
    ? rawPortLoadings.data
    : [];

  // Sync input when form value changes externally (edit mode)
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = portLoadings.filter((item) => {
    const country = item?.country || '';
    return country.toLowerCase().includes(query.toLowerCase());
  });

  const handleSelect = (item) => {
    const label = item?.country || '';
    setQuery(label);
    setOpen(false);
    onChange(label);
  };

  const handleClear = () => {
    setQuery('');
    setOpen(false);
    onChange('');
  };

  return (
    <>
      <div className="mb-2">
        <label
          htmlFor="supplier-origin"
          className="form-label fw-semibold small text-uppercase text-dark mb-1"
        >
          Origin
        </label>

        <div ref={wrapperRef} style={{ position: 'relative' }}>
          {/* Search input */}
          <div style={{ position: 'relative' }}>
            <input
              id="supplier-origin"
              type="text"
              className={`form-control pe-4${errors ? ' is-invalid' : ''}`}
              placeholder="Search origin..."
              value={query}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                onChange(e.target.value);
              }}
              onFocus={() => setOpen(true)}
              aria-label="Origin"
              aria-expanded={open}
              aria-haspopup="listbox"
              role="combobox"
            />
            {/* Clear button */}
            {query && (
              <button
                type="button"
                onClick={handleClear}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6c757d',
                  fontSize: '14px',
                  padding: 0,
                  lineHeight: 1,
                }}
                title="Clear"
                aria-label="Clear origin"
              >
                ✕
              </button>
            )}
          </div>

          {/* Dropdown list */}
          {open && (
            <div
              role="listbox"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1040,
                background: '#fff',
                border: '1px solid #ced4da',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                maxHeight: '220px',
                overflowY: 'auto',
              }}
            >
              {/* Options */}
              {filtered.length > 0 ? (
                filtered.map((item, i) => (
                  <div
                    key={i}
                    role="option"
                    aria-selected={query === item?.country}
                    onMouseDown={() => handleSelect(item)}
                    style={{
                      padding: '9px 12px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f4ff')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                  >
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        background: '#1A237E',
                        color: '#fff',
                        borderRadius: '4px',
                        padding: '1px 5px',
                        minWidth: '32px',
                        textAlign: 'center',
                        letterSpacing: '0.4px',
                      }}
                    >
                      {item?.countryCode || '—'}
                    </span>
                    <span>{item?.country}</span>
                  </div>
                ))
              ) : (
                <div style={{ padding: '9px 12px', fontSize: '13px', color: '#6c757d' }}>
                  No origins found
                </div>
              )}

              {/* Sticky "Add Origin" button at bottom */}
              <div
                style={{
                  borderTop: '1px solid #e9ecef',
                  position: 'sticky',
                  bottom: 0,
                  background: '#fff',
                }}
              >
                <button
                  type="button"
                  id="add-origin-dropdown-btn"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen(false);
                    setShowModal(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#1A237E',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f4ff')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  <span style={{ fontSize: '16px', lineHeight: 1 }}>＋</span>
                  Add Origin
                </button>
              </div>
            </div>
          )}
        </div>

        {errors && (
          <div className="invalid-feedback d-block" role="alert">
            {errors.message}
          </div>
        )}
      </div>

      {/* Add Origin Modal */}
      <AddOriginModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={() => refetch()}
      />
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
/**
 * AddSupplier — Supplier form (Create + Edit)
 * Edit mode is triggered by navigating here with location.state = supplierData.
 * Origin field is now a searchable dropdown backed by GET /portloading.
 * "Add Origin" in dropdown opens a modal that POSTs to /portloading and refreshes the list.
 */
const AddSupplier = () => {
  const { state: routeState } = useLocation();
  console.log('state', routeState);

  const editId = routeState?.id ?? null;
  const isEditMode = !!editId;
  const navigate = useNavigate();

  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues });

  const {
    fields: bankFields,
    append: appendBank,
    remove: removeBank,
  } = useFieldArray({ control, name: 'bankDetails' });

  useEffect(() => {
    if (isEditMode && routeState) {
      reset({
        supplierId: routeState.supplierId ?? defaultValues.supplierId,
        supplierName: routeState.supplierName ?? defaultValues.supplierName,
        ownerName: routeState.ownerName ?? defaultValues.ownerName,
        mobileNumber: routeState.mobileNumber ?? routeState.mobileNo ?? defaultValues.mobileNumber,
        emailId: routeState.emailId ?? defaultValues.emailId,
        panNumber: routeState.panNumber ?? defaultValues.panNumber,
        website: routeState.website ?? defaultValues.website,
        adCode: routeState.adCode ?? defaultValues.adCode,
        lutNumber: routeState.lutNumber ?? defaultValues.lutNumber,
        lutExpiryDate: routeState.lutExpiryDate ?? null,
        rcmcNumber: routeState.rcmcNumber ?? defaultValues.rcmcNumber,
        paymentTerms: routeState.paymentTerms ?? defaultValues.paymentTerms,
        incoterms: routeState.incoterms ?? defaultValues.incoterms,
        origin: routeState.origin ?? defaultValues.origin,
        foreignBankCharge: routeState.foreignBankCharge ?? defaultValues.foreignBankCharge,
        isActive: routeState.isActive ?? defaultValues.isActive,
        bankDetails: routeState.bankDetails?.length > 0 ? routeState.bankDetails : defaultValues.bankDetails,
      });
    }
  }, [isEditMode, routeState, reset]);

  const onSubmit = (data) => {
    console.log('onSubmit data', data);
    if (isEditMode) {
      updateMutation.mutate({ id: editId, data }, {
        onSuccess: () => navigate('/supplier/view'),
      });
    } else {
      createMutation.mutate(data, {
        onSuccess: () => navigate('/supplier/view'),
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="h-100 d-flex flex-column" noValidate>
      <div className="card shadow-sm border rounded-top-0 rounded-bottom-3 p-3 p-md-4 d-flex flex-column flex-grow-1">

        <div className="flex-grow-1">

          {/* ── 1. Supplier Details ───────────────────────────────────────── */}
          <Section title="Supplier Details">
            <div className="row gx-3 gy-2">
              <div className="col-12 col-md-6 col-lg-4">
                <Field
                  id="supplierName"
                  label="Supplier Name"
                  name="supplierName"
                  control={control}
                  placeholder="Enter supplier name"
                  rules={{ required: 'Supplier name is required.' }}
                  errors={errors.supplierName}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <Field
                  id="ownerName"
                  label="Owner Name"
                  name="ownerName"
                  control={control}
                  placeholder="Enter owner name"
                  errors={errors.ownerName}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-4">
                <Field
                  id="mobileNumber"
                  label="Mobile Number"
                  name="mobileNumber"
                  control={control}
                  type="tel"
                  placeholder="e.g. +91 9876543210"
                  rules={{
                    required: 'Mobile number is required.',
                    pattern: { value: PHONE_REGEX, message: 'Please enter a valid mobile number with country code.' },
                  }}
                  errors={errors.mobileNumber}
                />
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Field
                  id="emailId"
                  label="Email ID"
                  name="emailId"
                  control={control}
                  type="email"
                  placeholder="example@email.com"
                  rules={{ pattern: { value: EMAIL_REGEX, message: 'Enter a valid email.' } }}
                  errors={errors.emailId}
                />
              </div>

              {/* ── Origin — Searchable Dropdown ── */}
              <div className="col-12 col-md-6 col-lg-4">
                <Controller
                  name="origin"
                  control={control}
                  render={({ field }) => (
                    <OriginDropdown
                      value={field.value}
                      onChange={field.onChange}
                      errors={errors.origin}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Field
                  id="foreignBankCharge"
                  label="Foreign Bank Charge"
                  name="foreignBankCharge"
                  control={control}
                  placeholder="Enter foreign bank charge"
                  errors={errors.foreignBankCharge}
                />
              </div>
            </div>
          </Section>

          {/* ── 2. Bank Details (Dynamic Array) ──────────────────────────── */}
          <Section title="Bank Details">
            {bankFields.map((field, idx) => (
              <div key={field.id} className="border rounded-3 p-3 mb-2 gtss-bg-light">
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                  <h3 className="mb-0 fs-6 fw-bold text-uppercase gtss-text-primary">
                    Bank Account {idx + 1}
                  </h3>
                  {idx > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      aria-label={`Remove bank account ${idx + 1}`}
                      onClick={() => removeBank(idx)}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="row gx-3 gy-2">
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`accountHolderName-${idx}`}
                      label="Beneficiary Name"
                      name={`bankDetails.${idx}.accountHolderName`}
                      control={control}
                      placeholder="Enter Name"
                      errors={errors.bankDetails?.[idx]?.accountHolderName}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`accountHolderAddress-${idx}`}
                      label="Beneficiary Address"
                      name={`bankDetails.${idx}.accountHolderAddress`}
                      control={control}
                      placeholder="Enter Address"
                      errors={errors.bankDetails?.[idx]?.accountHolderAddress}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`bankName-${idx}`}
                      label="Bank Name"
                      name={`bankDetails.${idx}.bankName`}
                      control={control}
                      placeholder="Enter bank name"
                      errors={errors.bankDetails?.[idx]?.bankName}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`bankAddress-${idx}`}
                      label="Bank Address"
                      name={`bankDetails.${idx}.bankAddress`}
                      control={control}
                      placeholder="Enter bank address"
                      errors={errors.bankDetails?.[idx]?.bankAddress}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`swiftCode-${idx}`}
                      label="Swift Code"
                      name={`bankDetails.${idx}.swiftCode`}
                      control={control}
                      placeholder="Enter SWIFT code"
                      errors={errors.bankDetails?.[idx]?.swiftCode}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <Field
                      id={`accountNumber-${idx}`}
                      label="Account Number"
                      name={`bankDetails.${idx}.accountNumber`}
                      control={control}
                      placeholder="Enter A/C No"
                      errors={errors.bankDetails?.[idx]?.accountNumber}
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn w-100 fw-semibold text-white gtss-bg-secondary border-0 mb-2"
              aria-label="Add another bank account"
              onClick={() => appendBank({ ...EMPTY_BANK })}
            >
              + Add Bank Account
            </button>
          </Section>
        </div>

        {/* Submit */}
        <div className="text-center pt-2 pb-1 mt-auto">
          <button
            type="submit"
            className="btn px-5 py-2 fw-semibold text-white gtss-bg-primary border-0"
            disabled={isPending}
            aria-label="Submit supplier form"
          >
            {isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Saving...
              </>
            ) : isEditMode ? (
              'Update Supplier'
            ) : (
              'Submit'
            )}
          </button>
        </div>

      </div>
    </form>
  );
};

export default AddSupplier;